import styled from "@emotion/styled";
import { GrCheckbox, palette } from "@jnpmedi/grebob";
import { GrChip } from "@jnpmedi/grebob/dist/components/data/data-display/GrChip";
import { GrStack } from "@jnpmedi/grebob/dist/components/layout/GrStack";
import { CookieHelper } from "@jnpmedi/helper-session";
import { langHelper, MvnMessage } from "@newLibs/utils";
import { Markdown } from "./Markdown";
import { DOCS_NOTICE_POPUP_KEY, DOCS_POPUP_DATE_FORMAT } from "./Popup";
import Style from "./Popup.style";
import { COMMON_LANG } from "@maven-msg/lang/common";
import { MvnCodeFE } from "@maven-msg/lang/const";
import { MvnLanguage } from "@maven-msg/lib/Message";
import { isAdminNoticeLang, readableMavenProductServiceWithClinicalCloud, readableNoticeType } from "@maven-rest/admin/Notice.conv";
import { AdminNoticeType, AdminNoticeWithContent } from "@maven-rest/admin/Notice.dto";
import { LoLang } from "@maven-rest/common/common.types";
import { getCurrentDate } from "@maven-helper/functions/Misc.function";
import dayjs from "dayjs";

interface NoticePopupProps {
  notice: AdminNoticeWithContent;
  onClose: (id: bigint) => void;
}

export function NoticePopup(props: NoticePopupProps) {
  const { notice, onClose } = props;
  const { type, service, dueAt, title, content } = notice;

  const loLang = langHelper.getLang();
  const lang = isAdminNoticeLang(loLang) ? loLang : LoLang.KO;

  const handleNoticePopupDoNotShowToday = () => {
    const currentDate = getCurrentDate();

    CookieHelper.setCookie(
      DOCS_NOTICE_POPUP_KEY(notice.id),
      dayjs(currentDate).format(DOCS_POPUP_DATE_FORMAT),
      dayjs(dueAt).add(1, "day").toDate()
    );

    onClose(notice.id);
  };

  return (
    <Style.PopupWrapper>
      <GrStack height="calc(100% - 40px)" flexDirection="column">
        <Style.TextWrapper
          fontSize="1.25rem"
          fontWeight={700}
          style={{ whiteSpace: "wrap", wordBreak: "break-all", marginBottom: "1.5rem" }}
        >
          {MvnMessage(title as MvnCodeFE, undefined, undefined, MvnLanguage[lang])}
        </Style.TextWrapper>
        <StyledContentWrapper>
          <Style.PopupInfoWrapper style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <NoticeBadge type={type} />
            <Style.TextWrapper fontSize="1rem" fontWeight={700}>
              {readableMavenProductServiceWithClinicalCloud(service)}
            </Style.TextWrapper>
          </Style.PopupInfoWrapper>
          <Markdown text={MvnMessage(content as MvnCodeFE, undefined, undefined, MvnLanguage[lang])} />
        </StyledContentWrapper>
      </GrStack>
      <GrStack height="32px" margin="8px 0 0 0" justifyContent="space-between" alignItems="center">
        <GrCheckbox
          id="check"
          size="medium"
          type="rectangle"
          onChange={handleNoticePopupDoNotShowToday}
          style={{ padding: "0px", marginRight: "6px" }}
          label={{ text: MvnMessage(COMMON_LANG.DO_NOT_SHOW_TODAY), style: { fontSize: "13px", fontWeight: 400 } }}
        />
        <Style.CloseButton color="grey" onClick={() => onClose(notice.id)}>
          {MvnMessage({ en: "Close", ko: "닫기" })}
        </Style.CloseButton>
      </GrStack>
    </Style.PopupWrapper>
  );
}

const GrChipWrapper = styled("span")(
  ({ color }: { color: "red" | "green" }) =>
    color === "red" && {
      ".GrChip": {
        backgroundColor: palette.orange010,
        borderColor: palette.orange020,
      },
    }
);

function NoticeBadge(req: { type: AdminNoticeType }) {
  const badgeColor = (() => {
    switch (req.type) {
      case AdminNoticeType.ResolvedIncident:
        return "green";
      case AdminNoticeType.SystemOperation:
        return "red";
      default:
        throw new Error(`Unexpected flow, not supported notice type. (type: ${req.type})`);
    }
  })();

  return (
    <GrChipWrapper color={badgeColor}>
      <GrChip color={badgeColor} size="medium" variant="outlined-soft" shape="circle" label={readableNoticeType(req.type)} />
    </GrChipWrapper>
  );
}

const StyledContentWrapper = styled("div")({
  display: "flex",
  flexBasis: "calc(100% - 2.75rem)",
  flexDirection: "column",
  overflow: "auto",
});
